import React from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Header, Label, Message, Table } from 'semantic-ui-react'
import moment from 'moment'
import LoadingSpinner from '../../LoadingSpinner'
import { LOAD_EXTERNAL_EXPERTS_QUERY } from '../../../graphql/admin'
import { FOUND_STATUS_LABELS, displayName, registrationOf } from './helpers'
import ProjectBrief from './ProjectBrief'
import '../styles.css'
import './styles.css'

// De novo sourcing Stage 2, for review. Each row is an external expert the
// pipeline searched for; the detail page shows every page it found and whether
// each one is about them. Nothing here is a conclusion about the person — the point is to
// let a sourcer read what the web gave us and say what is missing.
const Sourcing = () => {
    const history = useHistory()
    const { data, loading, error } = useQuery(LOAD_EXTERNAL_EXPERTS_QUERY, {
        fetchPolicy: 'cache-and-network',
    })

    const externalExperts = data?.adminPortal?.externalExperts || []

    return (
        <div className="auth-users-page">
            <div className="auth-users-heading">
                <Header as="h2">De novo sourcing</Header>
                <p className="auth-user-muted">
                    External experts are people in the national provider
                    registry who are not yet our experts. For each one we search
                    the web and keep every page we find, marking which ones look
                    like the same person. Open a row to read the pages and tell
                    us what is useful and what is missing.
                </p>
            </div>

            <ProjectBrief />

            {error && <Message negative content={error.message} />}

            {loading && !data ? (
                <LoadingSpinner />
            ) : (
                <Table basic="very" selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>External expert</Table.HeaderCell>
                            <Table.HeaderCell>Specialty</Table.HeaderCell>
                            <Table.HeaderCell>Where</Table.HeaderCell>
                            <Table.HeaderCell>Employer</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">
                                Same-person pages / found
                            </Table.HeaderCell>
                            <Table.HeaderCell>
                                Did we find them?
                            </Table.HeaderCell>
                            <Table.HeaderCell>Last run</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {!error && externalExperts.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan="7">
                                    <Message
                                        info
                                        content="No external experts have been searched yet. Run denovo:enrichData to fill this."
                                    />
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {externalExperts.map((expert) => {
                            const registration = registrationOf(expert)
                            const samePerson = (expert.webSources || []).filter(
                                (page) => page.matchStatus === 'same_person'
                            ).length
                            const foundStatus =
                                FOUND_STATUS_LABELS[expert.foundStatus] ||
                                FOUND_STATUS_LABELS.no_search_results

                            return (
                                <Table.Row
                                    key={registration.number}
                                    onClick={() =>
                                        history.push(
                                            `/admin/sourcing/${registration.number}`
                                        )
                                    }
                                    className="sourcing-row"
                                >
                                    <Table.Cell>
                                        <strong>{displayName(expert)}</strong>
                                        <div className="auth-user-id">
                                            {registration.type}{' '}
                                            {registration.number}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {(expert.specialties || []).join(
                                            ', '
                                        ) || '—'}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {[expert.city, expert.region]
                                            .filter(Boolean)
                                            .join(', ') || '—'}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {(expert.organizations || [])[0] || '—'}
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <span className="sourcing-count">
                                            {samePerson}
                                        </span>
                                        <span className="auth-user-muted">
                                            {' '}
                                            / {(expert.webSources || []).length}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Label
                                            size="small"
                                            color={foundStatus.color}
                                            basic={foundStatus.basic}
                                        >
                                            {foundStatus.text}
                                        </Label>
                                    </Table.Cell>
                                    <Table.Cell className="auth-user-muted">
                                        {expert.lastAttemptedAt
                                            ? moment(
                                                  Number(expert.lastAttemptedAt)
                                              ).fromNow()
                                            : '—'}
                                    </Table.Cell>
                                </Table.Row>
                            )
                        })}
                    </Table.Body>
                </Table>
            )}
        </div>
    )
}

export default Sourcing
